import { motion } from "framer-motion";
import { useState } from "react";
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import MetaTags from "../components/SEO/MetaTags";
import { pageConfigs } from "../utils/seo";
import url from '../config';
import { Modal } from '../component/ui/Modal';

const TOPIC_OPTIONS = [
  "General question",
  "See Notation in action",
  "Partnership",
  "Talk through a challenge",
  "Careers",
  "Other",
];

const DEMO_BOOKING_URL =
  "https://bookings.cloud.microsoft/book/NotationAI@fownd.org/?ismsaljsauthenabled";

const Contact = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModel, setShowModel] = useState(false);

  const onSubmit = () => {
    if (!fullName.trim() || !email.trim() || !topic || !comment.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setLoading(true)
    const commentBody = [
      `Topic: ${topic}`,
      phone.trim() ? `Phone: ${phone.trim()}` : null,
      comment.trim(),
    ]
      .filter(Boolean)
      .join("\n\n");

    const body = {
      fullName: fullName.trim(),
      comment: commentBody,
      email: email.trim().toLowerCase(),
    };
    axios
      .post(`${url}/contact`, body)
      .then((res) => {
        setLoading(false);
        setShowModel(true);
        toast.success("Message Sent Successfully!!");
      })
      .catch((err) => {
        console.log(err?.response?.data?.errors[0]?.detail, "err");
        setLoading(false)
        toast.error(err?.response?.data?.errors[0]?.detail);
      });
  }

  return (
    <>
      <MetaTags
        title={pageConfigs.contact.title}
        description={pageConfigs.contact.description}
        keywords={pageConfigs.contact.keywords}
        canonical={pageConfigs.contact.canonical}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {/* Banner Section - No Buttons */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#1e3d3b]/20 to-[#1e3d3b]/40"></div>
          <div className="relative px-6 py-24 md:py-32 lg:py-40">
            <div className="mx-auto max-w-4xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight font-productSans">
                  Contact Us
                </h1>
                <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed font-dmSans">
                  We'd love to hear from you. Whether you have a question, a concern, or just want to learn more, our team is here to help.
                </p>
              </motion.div>
            </div>
          </div>
          <div className="absolute top-20 left-10 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        </section>

        {/* Message and schedule */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-slate-200"
            >
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-6 font-productSans">
                    Send us a Message
                  </h2>
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-semibold text-slate-700 mb-2 font-dmSans">
                        Name
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#619DD2] focus:border-transparent transition-all duration-300 font-dmSans"
                        placeholder="Your name"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-semibold text-slate-700 mb-2 font-dmSans">
                        Email
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#619DD2] focus:border-transparent transition-all duration-300 font-dmSans"
                        placeholder="you@clinic.com"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-semibold text-slate-700 mb-2 font-dmSans">
                        Phone <span className="font-normal text-slate-400">(optional)</span>
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#619DD2] focus:border-transparent transition-all duration-300 font-dmSans"
                        placeholder="(555) 555-5555"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-topic" className="block text-sm font-semibold text-slate-700 mb-2 font-dmSans">
                        Topic
                      </label>
                      <select
                        id="contact-topic"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#619DD2] focus:border-transparent transition-all duration-300 font-dmSans"
                        required
                      >
                        <option value="">Select a topic</option>
                        {TOPIC_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contact-comment" className="block text-sm font-semibold text-slate-700 mb-2 font-dmSans">
                        How can we help?
                      </label>
                      <textarea
                        id="contact-comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={5}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#619DD2] focus:border-transparent transition-all duration-300 font-dmSans resize-none"
                        placeholder="Tell us what you need"
                        required
                      />
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onSubmit}
                    disabled={loading}
                    className="mt-6 bg-[#619DD2] text-white px-10 py-3 text-base rounded-full font-semibold hover:shadow-xl transition-all duration-300 hover:bg-[#5a8bc2] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending..." : "Submit"}
                  </motion.button>
                </div>

                <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-200 flex flex-col">
                  <h2 className="text-2xl font-bold text-slate-800 mb-4 font-productSans">
                    Schedule a call
                  </h2>
                  <p className="text-slate-600 leading-relaxed font-dmSans">
                    Want to see Notation in action, explore a partnership, or talk through a challenge you're trying to solve? Let's connect.
                  </p>
                  <a
                    href={DEMO_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex"
                  >
                    <motion.span
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="inline-flex items-center justify-center bg-[#619DD2] text-white px-10 py-3 text-base rounded-full font-semibold hover:shadow-xl transition-all duration-300 hover:bg-[#5a8bc2]"
                    >
                      Schedule a call
                    </motion.span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Social Media Section - Horizontal Layout */}
        <section className="py-20 px-6 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 font-productSans">
                Follow along and stay connected
              </h2>
            </motion.div>

            {/* Horizontal Social Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Discord Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b] rounded-2xl p-6 text-white transition-all duration-300 hover:shadow-xl flex flex-col h-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-6 h-6 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.174.372.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418Z"/>
                  </svg>
                  <h3 className="text-lg font-bold">Discord</h3>
                </div>
                <p className="text-slate-200 mb-6 text-sm flex-grow">
                  Securely communicate, influence features and test the platform
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open("https://discord.gg/VcjDkXAwAt")}
                  className="bg-[#619DD2] text-white px-4 py-2 rounded-full font-semibold hover:bg-[#5a8bc2] transition-colors text-sm w-full mt-auto"
                >
                  Join
                </motion.button>
              </motion.div>

              {/* Facebook Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b] rounded-2xl p-6 text-white transition-all duration-300 hover:shadow-xl flex flex-col h-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-6 h-6 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <h3 className="text-lg font-bold">Facebook</h3>
                </div>
                <p className="text-slate-200 mb-6 text-sm flex-grow">
                  Support us, keep in touch and be in the know
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open("https://www.facebook.com/profile.php?id=100095255150461")}
                  className="bg-[#619DD2] text-white px-4 py-2 rounded-full font-semibold hover:bg-[#5a8bc2] transition-colors text-sm w-full mt-auto"
                >
                  Join
                </motion.button>
              </motion.div>

              {/* LinkedIn Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b] rounded-2xl p-6 text-white transition-all duration-300 hover:shadow-xl flex flex-col h-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-6 h-6 text-[#0077B5]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <h3 className="text-lg font-bold">LinkedIn</h3>
                </div>
                <p className="text-slate-200 mb-6 text-sm flex-grow">
                  Follow for latest updates, industry insights and company announcements
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open("https://www.linkedin.com/company/fownd-inc/posts/?feedView=all")}
                  className="bg-[#619DD2] text-white px-4 py-2 rounded-full font-semibold hover:bg-[#5a8bc2] transition-colors text-sm w-full mt-auto"
                >
                  Follow
                </motion.button>
              </motion.div>

              {/* Instagram Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b] rounded-2xl p-6 text-white transition-all duration-300 hover:shadow-xl flex flex-col h-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-6 h-6 text-[#E4405F]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.40z"/>
                  </svg>
                  <h3 className="text-lg font-bold">Instagram</h3>
                </div>
                <p className="text-slate-200 mb-6 text-sm flex-grow">
                  Check out visual stories, team highlights and behind-the-scenes content
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open("https://www.instagram.com/notation.fownd/")}
                  className="bg-[#619DD2] text-white px-4 py-2 rounded-full font-semibold hover:bg-[#5a8bc2] transition-colors text-sm w-full mt-auto"
                >
                  Follow
                </motion.button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Success Modal */}
        <Modal title="" show={showModel} onClose={() => navigate("/")}>
          <div className="px-10 mb-3 max-w-xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h3 className="font-productSans font-bold text-center m-auto text-2xl mb-5 text-slate-800">
                Thank you, we have received your response!
              </h3>
              <div className="font-dmSans font-normal text-center m-auto text-lg text-slate-600 mb-6">
                Join our discord group and get more updates!
              </div>
              <div className="flex items-center justify-center space-x-5 py-4 mb-6">
                <svg className="w-8 h-8 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.174.372.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418Z"/>
                </svg>
                <div className="font-dmSans font-bold text-lg text-slate-800">
                  Discord
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#619DD2] text-white font-bold font-dmSans rounded-full px-8 py-3 text-lg hover:bg-[#5a8bc2] transition-colors"
                onClick={() => window.open("https://discord.gg/VcjDkXAwAt")}
              >
                Join Discord
              </motion.button>
            </motion.div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default Contact;
